/**
 * BaseLayer
 *
 * Abstract base class for all layer types in Layer Drawer 2.0.
 * Provides a unified interface for managing layers on the map.
 *
 * All layer implementations (WMS, WFS, TMS, Live, GeoJSON) extend this class
 * and implement the abstract methods for their specific rendering logic.
 */

import mapboxgl from 'mapbox-gl';

/**
 * Layer configuration from database
 */
export interface LayerConfig {
  id: string;
  name: string;
  type: 'wms' | 'wfs' | 'tms' | 'live' | 'geojson' | 'mapbox-style' | 'var';
  enabled: boolean;
  category: 'backgrounds' | 'overlays' | 'live';
  layer_group: string | null;
  tags: string[];
  source_url: string | null;
  legend_url: string | null;
  zoom_min: number;
  zoom_max: number;
  default_opacity: number;
  default_order: number;
  metadata: Record<string, any>;
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
}

/**
 * Layer state
 */
export interface LayerState {
  isInitialized: boolean;
  isEnabled: boolean;
  currentOpacity: number;
  currentOrder: number;
  error: string | null;
}

/**
 * Layer event types
 */
export type LayerEventType = 'initialized' | 'enabled' | 'disabled' | 'error' | 'destroyed';

/**
 * Layer event handler
 */
export type LayerEventHandler = (layer: BaseLayer, data?: any) => void;

/**
 * Abstract base class for all layer types
 */
export abstract class BaseLayer {
  /**
   * Layer configuration from database
   */
  protected config: LayerConfig;

  /**
   * Mapbox GL map instance
   */
  protected map: mapboxgl.Map;

  /**
   * Layer state
   */
  protected state: LayerState;

  /**
   * Event handlers
   */
  private eventHandlers: Map<LayerEventType, Set<LayerEventHandler>>;

  /**
   * Constructor
   *
   * @param map Mapbox GL map instance
   * @param config Layer configuration from database
   */
  constructor(map: mapboxgl.Map, config: LayerConfig) {
    this.map = map;
    this.config = config;
    this.eventHandlers = new Map();

    // Initialize state
    this.state = {
      isInitialized: false,
      isEnabled: false,
      currentOpacity: config.default_opacity,
      currentOrder: config.default_order,
      error: null
    };
  }

  /**
   * Get layer ID
   */
  public getId(): string {
    return this.config.id;
  }

  /**
   * Get layer name
   */
  public getName(): string {
    return this.config.name;
  }

  /**
   * Get layer type
   */
  public getType(): string {
    return this.config.type;
  }

  /**
   * Get layer configuration
   */
  public getConfig(): LayerConfig {
    return { ...this.config };
  }

  /**
   * Get layer state
   */
  public getState(): LayerState {
    return { ...this.state };
  }

  /**
   * Check if layer is initialized
   */
  public isInitialized(): boolean {
    return this.state.isInitialized;
  }

  /**
   * Check if layer is enabled
   */
  public isEnabled(): boolean {
    return this.state.isEnabled;
  }

  /**
   * Initialize the layer
   *
   * This method should be called before enabling the layer.
   * Subclasses should implement the actual initialization logic.
   *
   * @returns Promise that resolves when initialization is complete
   */
  public async initialize(): Promise<void> {
    if (this.state.isInitialized) {
      return;
    }

    try {
      await this.onInitialize();
      this.state.isInitialized = true;
      this.state.error = null;
      this.emit('initialized');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      this.emit('error', { error: errorMessage });
      throw error;
    }
  }

  /**
   * Enable the layer (make it visible on the map)
   *
   * @returns Promise that resolves when layer is enabled
   */
  public async enable(): Promise<void> {
    if (!this.state.isInitialized) {
      await this.initialize();
    }

    if (this.state.isEnabled) {
      return;
    }

    try {
      await this.onEnable();
      this.state.isEnabled = true;
      this.state.error = null;
      this.emit('enabled');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      this.emit('error', { error: errorMessage });
      throw error;
    }
  }

  /**
   * Force re-enable the layer (used after style changes destroy layers)
   *
   * This method is called when a MapboxStyleLayer changes the base style,
   * which destroys all custom layers. It forces the layer to re-add itself
   * to the map even if it thinks it's already enabled.
   *
   * @returns Promise that resolves when layer is re-enabled
   */
  public async forceReEnable(): Promise<void> {
    // Mark as disabled so enable() will actually run
    this.state.isEnabled = false;

    // Re-enable the layer (this will re-add sources and layers to the map)
    await this.enable();
  }

  /**
   * Disable the layer (hide it from the map)
   *
   * @returns Promise that resolves when layer is disabled
   */
  public async disable(): Promise<void> {
    if (!this.state.isEnabled) {
      return;
    }

    try {
      await this.onDisable();
      this.state.isEnabled = false;
      this.state.error = null;
      this.emit('disabled');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.state.error = errorMessage;
      this.emit('error', { error: errorMessage });
      throw error;
    }
  }

  /**
   * Set layer opacity
   *
   * @param opacity Opacity value (0-1)
   */
  public async setOpacity(opacity: number): Promise<void> {
    if (opacity < 0 || opacity > 1) {
      throw new Error('Opacity must be between 0 and 1');
    }

    this.state.currentOpacity = opacity;

    if (this.state.isEnabled) {
      await this.onSetOpacity(opacity);
    }
  }

  /**
   * Get current opacity
   */
  public getOpacity(): number {
    return this.state.currentOpacity;
  }

  /**
   * Set layer order (z-index)
   *
   * @param order Order value (higher = on top)
   */
  public setOrder(order: number): void {
    this.state.currentOrder = order;

    if (this.state.isEnabled) {
      this.onSetOrder(order);
    }
  }

  /**
   * Get current order
   */
  public getOrder(): number {
    return this.state.currentOrder;
  }

  /**
   * Destroy the layer and clean up resources
   */
  public async destroy(): Promise<void> {
    if (this.state.isEnabled) {
      await this.disable();
    }

    await this.onDestroy();
    this.eventHandlers.clear();
    this.emit('destroyed');
  }

  /**
   * Register event handler
   *
   * @param event Event type
   * @param handler Event handler function
   */
  public on(event: LayerEventType, handler: LayerEventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Unregister event handler
   *
   * @param event Event type
   * @param handler Event handler function
   */
  public off(event: LayerEventType, handler: LayerEventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit event to all registered handlers
   *
   * @param event Event type
   * @param data Optional event data
   */
  protected emit(event: LayerEventType, data?: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(this, data));
    }
  }

  /**
   * Abstract method: Initialize layer-specific resources
   *
   * Subclasses must implement this method to perform initialization
   * specific to their layer type (e.g., create sources, validate config).
   */
  protected abstract onInitialize(): Promise<void>;

  /**
   * Abstract method: Enable layer on map
   *
   * Subclasses must implement this method to add the layer to the map
   * and make it visible.
   */
  protected abstract onEnable(): Promise<void>;

  /**
   * Abstract method: Disable layer on map
   *
   * Subclasses must implement this method to hide the layer from the map
   * without destroying resources.
   */
  protected abstract onDisable(): Promise<void>;

  /**
   * Abstract method: Update layer opacity
   *
   * @param opacity Opacity value (0-1)
   */
  protected abstract onSetOpacity(opacity: number): Promise<void>;

  /**
   * Abstract method: Update layer order
   *
   * @param order Order value (higher = on top)
   */
  protected abstract onSetOrder(order: number): void;

  /**
   * Abstract method: Destroy layer and clean up resources
   *
   * Subclasses must implement this method to remove all layer resources
   * from the map and clean up any event listeners or timers.
   */
  protected abstract onDestroy(): Promise<void>;

  /**
   * Get the Mapbox GL layer IDs associated with this layer
   *
   * This is used for managing layer order and removing layers.
   * Subclasses should return all Mapbox layer IDs they create.
   *
   * @returns Array of Mapbox layer IDs
   */
  public abstract getMapboxLayerIds(): string[];

  /**
   * Get the Mapbox GL source IDs associated with this layer
   *
   * This is used for removing sources when the layer is destroyed.
   * Subclasses should return all Mapbox source IDs they create.
   *
   * @returns Array of Mapbox source IDs
   */
  public abstract getMapboxSourceIds(): string[];
}
